import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AD_TYPES, GOALS, SB_BRAND_AD_TYPE } from "../../../components/core/blocks/AdPlan/constants";
import type { TSelector } from "../../../i18n/types";

import NoImage from "../../../assets/images/utility/no_image.png";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import moment from "moment";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { formatCurrency } from "../../../utils/formatNumber";

import TitleLink from "../../../components/core/basic/TitleLink";
import { getDashboardTo } from "../../../views/AdvertisingOverview";
import { useAppSelector } from "../../../redux/hooks";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { css, cx } from "../../../../styled-system/css";
import { type GVAdvertisement } from "../types";
import { type ReactElement, useMemo } from "react";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { OverflowTags } from "../../../components/core/blocks/OverflowTags";
const URL_PREFIX = getURLPrefix();

const SB_FORMAT_LABELS: Record<string, TSelector> = {
  [SB_BRAND_AD_TYPE.PRODUCT_COLLECTION]: ($) => $.advertising["Sponsored Brand: Product Collection"],
  [SB_BRAND_AD_TYPE.MANUAL_COLLECTION]: ($) => $.advertising["Sponsored Brand: Manual Collection"],
  [SB_BRAND_AD_TYPE.AUTO_COLLECTION]: ($) => $.advertising["Sponsored Brand: Auto Collection"],
};

const getAdTypeLabel = (ad_type?: number, creative?: { brand_ad_type: string }): TSelector | undefined => {
  if (ad_type === undefined) return undefined;

  if (ad_type === AD_TYPES.SPONSORED_PRODUCT_WALMART.value && localStorage.channel === "walmart") {
    return AD_TYPES.SPONSORED_PRODUCT_WALMART.label;
  }
  if (ad_type === AD_TYPES.SPONSORED_BRAND_V2.value) {
    const brandAdType = creative?.brand_ad_type ?? "";
    if (([SB_BRAND_AD_TYPE.BRAND_VIDEO, SB_BRAND_AD_TYPE.VIDEO] as string[]).includes(brandAdType)) {
      return AD_TYPES.SPONSORED_BRAND_VIDEO.label;
    }
    if (SB_FORMAT_LABELS[brandAdType]) {
      return SB_FORMAT_LABELS[brandAdType];
    }
  }
  return Object.values(AD_TYPES).find((adTypeId) => ad_type === adTypeId.value)?.label;
};

interface Props {
  ad: GVAdvertisement;
  seasonalCategory?: boolean;
  className?: string;
}

export const AdvertisementCell = ({ ad, seasonalCategory, className }: Props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const { t } = useTranslation(["advertising"]);
  const fmt = useDateFormatter("monthDay");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const adId = ad.ad_id;
  const image = `${URL_PREFIX}/api/productimage/?advertisement=${adId}`;

  const displayAdStatus = ():
    | {
        icon: ReactElement;
        text: string;
        color: string;
      }
    | undefined => {
    const status_id = ad.ad_status;
    const state_id = ad.state;
    const moderation_status = ad.moderation_status;
    const ad_type = ad.ad_type;
    const ad_serving_status = ad.serving_status;
    if (status_id === undefined || state_id === undefined) return undefined;

    const success = <i className="pe-7s-check" />;
    const progress = <i className="pe-7s-more" />;
    const failed = <i className="pe-7s-attention" />;
    const paused = <i className="pe-7s-close-circle" />;
    const ended = <i className="pe-7s-less" />;
    if (ad_type === AD_TYPES.SPONSORED_DISPLAY_V2.value && typeof moderation_status === "string") {
      return { icon: failed, text: t(($) => $.advertising["Creative Failed"]), color: "red" };
    } else if (ad_serving_status === "ENDED") {
      return { icon: ended, text: t(($) => $.advertising["Ended"]), color: "purple" };
    } else if (status_id === 1) {
      return { icon: success, text: t(($) => $.advertising["Success"]), color: "green" };
    } else if (status_id === 10) {
      return { icon: failed, text: t(($) => $.advertising["Failed"]), color: "red" };
    } else if (status_id === 2) {
      return { icon: progress, text: t(($) => $.advertising["Pending"]), color: "orange" };
    } else if (status_id === 3 || (status_id === 11 && state_id === 0)) {
      return { icon: paused, text: t(($) => $.advertising["Paused"]), color: "blue" };
    } else if (status_id === 11) {
      return { icon: success, text: t(($) => $.advertising["Approved"]), color: "green" };
    } else if (status_id === 22) {
      return { icon: progress, text: t(($) => $.advertising["In Progress"]), color: "orange" };
    } else if (status_id === 20) {
      return { icon: failed, text: t(($) => $.advertising["Rejected"]), color: "red" };
    }
  };

  const adStatusDisplay = displayAdStatus();
  const adTypeLabel = getAdTypeLabel(ad.ad_type, ad.creative);

  let NTMBadge = "hidden";

  if (ad.ad_goal === GOALS.IMPORTED_ADS.value) NTMBadge = "non-managed";
  if (ad.non_trellis_enrolled_ad) NTMBadge = "enrolled";
  if (ad.non_trellis_enrolled_ad_accept_bids) NTMBadge = "accept_bids";

  const showNtmBadge = !tagsEnhancedEnabled && NTMBadge !== "hidden";
  const showMultiMarketplaceBadge = ad.multi_marketplace;

  let { scheduled_actions: scheduledActions, tags, tags_enhanced, program_name: biddingProgramName } = ad;

  const filteredScheduledActions = useMemo(() => {
    return (
      scheduledActions?.filter(
        (sa) =>
          sa.scheduled_action_status !== undefined && ["scheduled", "active"].includes(sa.scheduled_action_status),
      ) ?? []
    );
  }, [scheduledActions]);

  if (!biddingProgramName && ad.program_name) {
    biddingProgramName = ad.program_name;
  }

  return (
    <div className={cx(css({ display: "flex", fontSize: "1.5rem", fontWeight: "500" }), className)}>
      <div
        className={css({
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          userSelect: "none",
        })}
        style={{ background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain` }}
        draggable="false"
      />
      <div className="overflow-ellipsis pt-2 title-link" style={{ textAlign: "left" }}>
        <TitleLink
          to={getDashboardTo({
            type: "advertisement",
            id: adId,
          })}
          title={ad.ad_name}
        />
        {(adTypeLabel ||
          adStatusDisplay ||
          showNtmBadge ||
          showMultiMarketplaceBadge ||
          ad.campaign_status ||
          ad.start_date ||
          ad.end_date ||
          seasonalCategory) && (
          <div className="d-flex align-items-center">
            {(adTypeLabel || adStatusDisplay) && (
              <small
                className="text-nowrap pe-3"
                style={{
                  fontWeight: "light",
                  color: "#666",
                  fontSize: "1rem",
                }}
              >
                {adTypeLabel && <i>{t(adTypeLabel)}</i>}
                <span
                  style={{
                    display: "inline-block",
                    fontWeight: "bold",
                    marginLeft: "1rem",
                    fontSize: "18px",
                    verticalAlign: "middle",
                  }}
                >
                  {adStatusDisplay && (
                    <HoverInfo icon={adStatusDisplay.icon} iconCss={{ color: adStatusDisplay.color }}>
                      {adStatusDisplay.text}
                    </HoverInfo>
                  )}
                </span>
              </small>
            )}
            {showNtmBadge && NTMBadge === "non-managed" && (
              <Badge className="bg-trellis-purple me-2" pill>
                {t(($) => $.advertising["Non Managed"])}
              </Badge>
            )}
            {showNtmBadge && NTMBadge === "enrolled" && (
              <Badge className="bg-trellis-fuchsia me-2" pill>
                {t(($) => $.advertising["NTM - Enrolled"])}
              </Badge>
            )}
            {showNtmBadge && NTMBadge === "accept_bids" && (
              <Badge className="bg-trellis-green me-2" pill>
                {t(($) => $.advertising["NTM - Bids Accepted"])}
              </Badge>
            )}
            {showMultiMarketplaceBadge && (
              <Badge className="bg-light-pink me-2" pill>
                {t(($) => $.advertising["Multi Marketplace"])}
              </Badge>
            )}
            {ad.campaign_status === "CAMPAIGN_OUT_OF_BUDGET" ?
              <Badge
                className={`badge bg-trellis-purple me-2`}
                pill
                style={{
                  borderRadius: "5px",
                }}
              >
                <span className="fs-6 ">{t(($) => $.advertising["Out of Budget"])} </span>
              </Badge>
            : null}

            {ad.start_date || ad.end_date ?
              <OverlayTrigger
                overlay={
                  <Tooltip placement="top" className="in" id="tooltip-top" bsPrefix="tr-popover-alt bg-purple">
                    <span className="text-center mx-auto fs-5">
                      {t(($) => $.advertising["Manual budget change scheduled"])} <br />
                      {fmt.format(moment(ad.start_date).toDate())} - {fmt.format(moment(ad.end_date).toDate())}
                    </span>
                  </Tooltip>
                }
              >
                <Badge
                  className={`badge bg-trellis-mauve me-2`}
                  pill
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  <span className="fs-6 ">{t(($) => $.advertising["Scheduled Bids"])} </span>
                </Badge>
              </OverlayTrigger>
            : null}

            {seasonalCategory ?
              <Badge className="bg-trellis-fuchsia" pill>
                {t(($) => $.advertising["Seasonality"])}
              </Badge>
            : null}
          </div>
        )}
        {filteredScheduledActions.length ?
          filteredScheduledActions.map((scheduledAction, index) => (
            <Badge
              key={`scheduledActionAd${ad.ad_id}-${index}`}
              className={`badge bg-trellis-purple me-3`}
              pill
              style={{
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              <span className="fs-6 ">
                {t(($) => $.advertising["{amount} Budget Update: {startDate}-{endDate}"], {
                  amount:
                    scheduledAction.scheduled_action_fields[0]?.change_type === "percentage_change" ?
                      `${(scheduledAction.scheduled_action_fields[0].change_value ?? 0) > 1 ? "+" : ""}${Math.trunc(Math.round((scheduledAction.scheduled_action_fields[0].change_value ?? 0) * 100) - 100)}%`
                    : formatCurrency(scheduledAction.scheduled_action_fields[0]?.change_value ?? 0, {
                        marketPlace: marketPlace,
                        addPlus: true,
                      }),
                  startDate: fmt.format(moment(scheduledAction.scheduled_action_start_date).utc().toDate()),
                  endDate: fmt.format(moment(scheduledAction.scheduled_action_end_date).utc().toDate()),
                })}{" "}
              </span>
            </Badge>
          ))
        : null}
        {biddingProgramName ?
          <Badge className="bg-trellis-fuchsia me-3" pill>
            {t(($) => $.advertising["Bidding Program: {name}"], { name: biddingProgramName })}
          </Badge>
        : null}
        {!tagsEnhancedEnabled && (ad.day_parting_method ?? 0) > 0 ?
          <Badge className="bg-trellis-fuchsia me-3" pill>
            {t(($) => $.advertising["Dayparting"])}
          </Badge>
        : null}
        <OverflowTags enhancedTags={tags_enhanced} tags={tags} id={ad.ad_id} />
      </div>
    </div>
  );
};
